/**
 * Cache em memória da sessão, compartilhado entre `src-next` e o legado
 * sem Provider. A entrada vale até `invalidate` ou um `getOrFetch` forçado.
 * Não há TTL: recolher e expandir de novo não gera outra rede.
 */

export interface MemoryCacheEntry<T> {
  data: T;
  updatedAt: number;
}

export interface MemoryCacheSnapshot<T> {
  entry: MemoryCacheEntry<T> | null;
  inFlight: boolean;
  error: unknown;
}

type Listener = () => void;

const IDLE_SNAPSHOT: MemoryCacheSnapshot<never> = {
  entry: null,
  inFlight: false,
  error: null,
};

export interface MemoryCache {
  get<T>(key: string): MemoryCacheEntry<T> | undefined;
  set<T>(key: string, data: T): MemoryCacheEntry<T>;
  invalidate(key: string): void;
  invalidatePrefix(prefix: string): void;
  /**
   * Lê a memória ou busca uma vez. Chamadas simultâneas da mesma chave
   * compartilham a mesma Promise (também no refresh forçado em paralelo).
   */
  getOrFetch<T>(
    key: string,
    fetcher: () => Promise<T>,
    options?: { force?: boolean },
  ): Promise<T>;
  subscribe(listener: Listener): () => void;
  /** Referência estável enquanto esta chave não mudar. */
  getKeySnapshot<T>(key: string): MemoryCacheSnapshot<T>;
}

export function createMemoryCache(): MemoryCache {
  const snapshots = new Map<string, MemoryCacheSnapshot<unknown>>();
  const inflight = new Map<string, Promise<unknown>>();
  const forceInflight = new Set<string>();
  const generations = new Map<string, number>();
  const listeners = new Set<Listener>();

  function emit() {
    for (const listener of [...listeners]) listener();
  }

  function publish(key: string, snapshot: MemoryCacheSnapshot<unknown>) {
    snapshots.set(key, snapshot);
    emit();
  }

  function getKeySnapshot<T>(key: string): MemoryCacheSnapshot<T> {
    return (snapshots.get(key) as MemoryCacheSnapshot<T> | undefined) ?? IDLE_SNAPSHOT;
  }

  function get<T>(key: string): MemoryCacheEntry<T> | undefined {
    const entry = snapshots.get(key)?.entry;
    return entry as MemoryCacheEntry<T> | undefined;
  }

  function set<T>(key: string, data: T): MemoryCacheEntry<T> {
    generations.set(key, (generations.get(key) ?? 0) + 1);
    inflight.delete(key);
    forceInflight.delete(key);
    const entry = { data, updatedAt: Date.now() };
    publish(key, { entry, inFlight: false, error: null });
    return entry;
  }

  function invalidate(key: string) {
    generations.set(key, (generations.get(key) ?? 0) + 1);
    inflight.delete(key);
    forceInflight.delete(key);
    if (!snapshots.has(key)) return;
    publish(key, { entry: null, inFlight: false, error: null });
  }

  function invalidatePrefix(prefix: string) {
    for (const key of [...snapshots.keys()]) {
      if (key.startsWith(prefix)) invalidate(key);
    }
  }

  function getOrFetch<T>(
    key: string,
    fetcher: () => Promise<T>,
    options?: { force?: boolean },
  ): Promise<T> {
    const force = Boolean(options?.force);

    if (!force) {
      const pending = inflight.get(key);
      if (pending) return pending as Promise<T>;
      const cached = snapshots.get(key)?.entry;
      if (cached) return Promise.resolve(cached.data as T);
    } else if (forceInflight.has(key)) {
      const pending = inflight.get(key);
      if (pending) return pending as Promise<T>;
    }

    const generation = (generations.get(key) ?? 0) + 1;
    generations.set(key, generation);
    const previous = snapshots.get(key);
    publish(key, {
      entry: previous?.entry ?? null,
      inFlight: true,
      error: null,
    });

    let promise!: Promise<T>;
    promise = (async () => {
      try {
        const data = await fetcher();
        if (generations.get(key) !== generation) return data;
        publish(key, {
          entry: { data, updatedAt: Date.now() },
          inFlight: false,
          error: null,
        });
        return data;
      } catch (error) {
        if (generations.get(key) === generation) {
          const latest = snapshots.get(key);
          publish(key, {
            entry: latest?.entry ?? null,
            inFlight: false,
            error,
          });
        }
        throw error;
      } finally {
        if (inflight.get(key) === promise) {
          inflight.delete(key);
          forceInflight.delete(key);
        }
      }
    })();

    inflight.set(key, promise);
    if (force) forceInflight.add(key);
    return promise;
  }

  function subscribe(listener: Listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  }

  return {
    get,
    set,
    invalidate,
    invalidatePrefix,
    getOrFetch,
    subscribe,
    getKeySnapshot,
  };
}

/** Instância única da sessão. Visível no FluxZap e no dashboard legado. */
export const sharedMemoryCache = createMemoryCache();
